import config from 'config/Config';
import BaseApi from './BaseApi';
import {
  ProjectTemplate,
  UserSummary,
  LearningTeamSummary,
} from 'types/store/normalized';
import { logger } from 'utils/logger';

export default class ProjectTemplatesApi extends BaseApi<never> {
  constructor() {
    super(`${config.get('backendURL')}project-templates/`, 'cognota-be');
  }

  async getAssignedTeams(templateId: string): Promise<string[]> {
    const { data } = await this.axiosInstance.get<{
      data: { type: string; id: string }[];
    }>(`${templateId}/relationships/assigned-teams`);
    return (data.data ?? []).map((resource) => resource.id);
  }

  async addTeams(templateId: string, teamIds: string[]): Promise<void> {
    await this.axiosInstance.post(
      `${templateId}/relationships/assigned-teams`,
      {
        data: teamIds.map((id) => ({ type: 'learning-teams', id })),
      }
    );
  }

  async removeTeams(templateId: string, teamIds: string[]): Promise<void> {
    await this.axiosInstance.delete(
      `${templateId}/relationships/assigned-teams`,
      {
        data: {
          data: teamIds.map((id) => ({ type: 'learning-teams', id })),
        },
      }
    );
  }

  async getAll(): Promise<ProjectTemplate[]> {
    const { data } = await this.axiosInstance.get(
      '?include=owner,assigned-teams'
    );
    return this.parseCollection(data.data ?? [], data.included ?? []);
  }

  async getForSelection(): Promise<ProjectTemplate[]> {
    const { data } = await this.axiosInstance.get(
      '?filter[project-templates-eligible-for-selection]=true'
    );
    return this.parseResources(data.data ?? []);
  }

  private parseCollection(
    resources: any[],
    included: any[]
  ): ProjectTemplate[] {
    return resources.map((resource) => {
      const attrs = resource.attributes;
      const ownerRef = resource.relationships?.owner?.data;
      const teamRefs = resource.relationships?.['assigned-teams']?.data ?? [];

      return {
        id: resource.id,
        name: attrs.name,
        description: attrs.description ?? null,
        ownerId: attrs.ownerId ?? null,
        access: attrs.access,
        status: attrs.status,
        resourceType: attrs.resourceType,
        system: attrs.system,
        exclusive: attrs.exclusive,
        hidden: attrs.hidden,
        creatorId: attrs.creatorId ?? null,
        createdAt: attrs.createdAt,
        updatedAt: attrs.updatedAt,
        owner: ownerRef ? this.hydrateOwner(ownerRef.id, included) : null,
        assignedTeams: teamRefs
          .map((ref: any) => this.hydrateTeam(ref.id, included))
          .filter(
            (team: LearningTeamSummary | null): team is LearningTeamSummary =>
              team !== null
          ),
      };
    });
  }

  private parseResources(resources: any[]): ProjectTemplate[] {
    return resources.map((resource) => {
      const attrs = resource.attributes;
      return {
        id: resource.id,
        name: attrs.name,
        description: attrs.description ?? null,
        ownerId: attrs.ownerId ?? null,
        access: attrs.access,
        status: attrs.status,
        resourceType: attrs.resourceType,
        system: attrs.system,
        exclusive: attrs.exclusive,
        hidden: attrs.hidden,
        creatorId: attrs.creatorId ?? null,
        createdAt: attrs.createdAt,
        updatedAt: attrs.updatedAt,
        owner: null,
        assignedTeams: [],
      };
    });
  }

  private hydrateOwner(id: string, included: any[]): UserSummary | null {
    const resource = included.find(
      (r) => r.type === 'org-members' && r.id === id
    );
    if (!resource) {
      logger.warn(
        `ProjectTemplatesApi: owner "${id}" referenced in relationship but missing from included resources`
      );
      return null;
    }
    const attrs = resource.attributes;
    const firstName = attrs.firstName ?? '';
    const lastName = attrs.lastName ?? '';
    return {
      id: resource.id,
      name: `${firstName} ${lastName}`.trim(),
      firstName,
      lastName,
      email: attrs.email ?? '',
      status: attrs.status ?? null,
      avatarUrl: attrs.avatarUrl ?? null,
      userType: attrs.userType ?? '',
    };
  }

  private hydrateTeam(id: string, included: any[]): LearningTeamSummary | null {
    const resource = included.find(
      (r) => r.type === 'learning-teams' && r.id === id
    );
    if (!resource) {
      logger.warn(
        `ProjectTemplatesApi: team "${id}" referenced in relationship but missing from included resources`
      );
      return null;
    }
    return { id: resource.id, name: resource.attributes.name };
  }
}
